import {trackEvent} from '@hconnect/common/logging/Analytics'
import {ExternalLink, Icon, TextSmallRegular} from '@hm/fundamental'
import {mdiFilePdfBox} from '@mdi/js'
import moment from 'moment'
import {useTranslation} from 'react-i18next'
import {v4 as uuidV4} from 'uuid'

import {Entity, EntityPage, getDocumentFileName} from '../../../../Organisms/Documents'
import {useFeaturesState} from '../../../../Organisms/Features'
import {LinkMedia} from '../Delivery.types'
import {FinanceAnalytics, Invoice} from '../Invoice.types'
import {findInvoiceDocument} from '../Invoice.utils'

import {KSeFInvoice} from './KSeFInvoice'

type InvoiceNumberProps = {
  invoice: Invoice
  isCustomerInvoiceNumber: boolean
  downloadDocument: (jobId: string, documentId: string, fileName: string) => void
  analytics: FinanceAnalytics
}

export const InvoiceNumber = ({
  invoice,
  isCustomerInvoiceNumber,
  downloadDocument,
  analytics
}: InvoiceNumberProps) => {
  const {t} = useTranslation()
  const {getFeature} = useFeaturesState()
  const isKSeFEnabled =
    getFeature('KSeFInvoice') &&
    invoice.electronicInvoiceNr &&
    invoice.electronicInvoiceSecureHash &&
    invoice.electronicInvoiceDate

  const invoicePdf = findInvoiceDocument(invoice)
  const invoiceNumber =
    isCustomerInvoiceNumber && invoice.customerInvoiceNumber
      ? invoice.customerInvoiceNumber
      : invoice.invoiceNumber
  const documentId = invoicePdf ? invoicePdf.href.split('/')[1] : undefined
  const fileName = invoicePdf
    ? getDocumentFileName(EntityPage.invoices, Entity.invoice, invoicePdf.media, t, {
        entityNumber: invoiceNumber
      })
    : `${Entity.invoice}.${LinkMedia.Pdf}`

  if (isKSeFEnabled) {
    return (
      <div className="flex flex-col gap-1 items-start">
        <KSeFInvoice invoice={invoice} />
        <div>{invoiceNumber}</div>
      </div>
    )
  }

  return documentId ? (
    <div className="flex flex-col">
      <a
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          const jobId: string = uuidV4()
          trackEvent('hubDownloadSingle', {
            product: 'hub',
            jobId: jobId,
            downloadedItem: 'invoice',
            linkToDownloadedItem: invoicePdf?.href,
            customerId: analytics.customerId,
            customerName: analytics.customerName,
            entryPoint: 'Invoice List',
            filteredDateRange: {startDate: analytics.startDate, endDate: analytics.endDate},
            salesOrg: invoice.orgUnitId,
            issuedDate: invoice.invoiceDate,
            userId: analytics.userId,
            userCountry: analytics.userCountry,
            userRoles: analytics.userRoles,
            userIsInternal: analytics.userIsInternal,
            downloadedItemBusinessLine: invoice.businessLine
          })
          downloadDocument(jobId, documentId, fileName)
        }}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline cursor-pointer text-primary"
      >
        <div className="inline-flex decoration-inherit gap-1">
          <Icon data-test-id={`pdficon-${invoice.invoiceId}`} path={mdiFilePdfBox} />
          {invoiceNumber}
        </div>
      </a>
      {invoice.isFinanceAccountingInvoice && invoice.customerInvoiceNumber && (
        <div className="flex">
          {t('invoiceDetail.externalRef', {externalReference: invoice.customerInvoiceNumber})}
        </div>
      )}
    </div>
  ) : (
    <div className="flex flex-col">
      {invoiceNumber}
      {invoice.isFinanceAccountingInvoice && invoice.customerInvoiceNumber && (
        <div className="flex">
          {t('invoiceDetail.externalRef', {externalReference: invoice.customerInvoiceNumber})}
        </div>
      )}
    </div>
  )
}
